/**
 * Format currency based on currency type
 * @param {number} amount - Amount to format
 * @param {string} currency - Currency code (VND, USD, EUR)
 * @returns {string} Formatted currency string
 */
export const formatCurrency = (amount, currency = 'VND') => {
  if (!amount && amount !== 0) return '';
  
  const locale = getCurrencyLocale(currency);
  const symbol = getCurrencySymbol(currency);
  
  return new Intl.NumberFormat(locale).format(amount) + ' ' + symbol;
};

/**
 * Format currency in compact form (K, M, B)
 * @param {number} amount - Amount to format
 * @param {string} currency - Currency code
 * @returns {string} Compact formatted string
 */
export const formatCurrencyCompact = (amount, currency = 'VND') => {
  if (!amount && amount !== 0) return '–';
  
  const symbol = getCurrencySymbol(currency);
  
  if (amount >= 1_000_000_000) {
    return (amount / 1_000_000_000).toFixed(1).replace('.0', '') + 'B ' + symbol;
  }
  if (amount >= 1_000_000) {
    return (amount / 1_000_000).toFixed(1).replace('.0', '') + 'M ' + symbol;
  }
  if (amount >= 1_000) {
    return (amount / 1_000).toFixed(0) + 'K ' + symbol;
  }
  
  return amount.toLocaleString() + ' ' + symbol;
};

/**
 * Get locale for currency
 * @param {string} currency - Currency code
 * @returns {string} Locale code
 */
export const getCurrencyLocale = (currency) => {
  const localeMap = {
    'VND': 'vi-VN',
    'USD': 'en-US',
    'EUR': 'de-DE',
    'GBP': 'en-GB',
    'JPY': 'ja-JP',
    'CNY': 'zh-CN'
  };
  
  return localeMap[currency] || 'vi-VN';
};

/**
 * Get currency symbol
 * @param {string} currency - Currency code
 * @returns {string} Currency symbol
 */
export const getCurrencySymbol = (currency) => {
  const symbolMap = {
    'VND': 'đ',
    'USD': '$',
    'EUR': '€',
    'GBP': '£',
    'JPY': '¥',
    'CNY': '¥'
  };
  
  return symbolMap[currency] || 'đ';
};

/**
 * Parse smart money input (supports k, tr, m, b)
 * @param {string|number} val - Input value
 * @param {number} oldVal - Old value for relative changes (+/-)
 * @returns {number} Parsed amount
 */
export const parseSmartMoney = (val, oldVal = 0) => {
  if (typeof val !== 'string') return Number(val) || 0;

  let v = val.trim().toLowerCase();
  let sign = 1;

  if (v.startsWith('+')) { sign = 1; v = v.slice(1); }
  if (v.startsWith('-')) { sign = -1; v = v.slice(1); }

  let mul = 1;
  let hasSuffix = true;
  if (v.endsWith('triệu') || v.endsWith('tr') || v.endsWith('t')) {
    mul = 1_000_000;
    v = v.replace(/triệu|tr|t$/g, '');
  } else if (v.endsWith('m')) {
    mul = 1_000_000;
    v = v.slice(0, -1);
  } else if (v.endsWith('nghìn') || v.endsWith('k')) {
    mul = 1_000;
    v = v.replace(/nghìn|k$/g, '');
  } else if (v.endsWith('tỷ') || v.endsWith('ty') || v.endsWith('b')) {
    mul = 1_000_000_000;
    v = v.replace(/tỷ|ty|b$/g, '');
  } else {
    hasSuffix = false;
  }

  // Bỏ ký hiệu tiền tệ và khoảng trắng
  v = v.replace(/[₫$€£¥\s]/g, '');

  if (hasSuffix) {
    // CÓ hậu tố (tr/k/tỷ...): dấu chấm/phẩy là DẤU THẬP PHÂN.
    // Lỗi cũ: xoá dấu chấm sau khi tách hậu tố → "1.5tr" thành 15 × 1tr = 15tr (×10!)
    v = v.replace(',', '.');
  } else {
    // KHÔNG hậu tố: dấu chấm/phẩy là phân cách hàng nghìn ("1.500.000")
    v = v.replace(/[.,]/g, '');
  }

  let num = parseFloat(v);
  if (isNaN(num)) num = 0;

  let result = sign * num * mul;

  // If starts with +/-, treat as relative change
  if (val.trim().startsWith('+') || val.trim().startsWith('-')) {
    result = oldVal + result;
  }

  return Math.round(result);
};

/**
 * Format money in full form theo tiền tệ (mặc định VND để tương thích ngược)
 * @param {number} value - Amount to format
 * @param {string} currency - Currency code
 * @returns {string} Formatted money string
 */
export const formatMoneyFull = (value, currency = 'VND') => {
  const symbol = getCurrencySymbol(currency);
  const locale = getCurrencyLocale(currency);
  if (!value) return `0 ${symbol}`;
  return value.toLocaleString(locale) + ' ' + symbol;
};
